/**
 * Welche Herkunft ein Seitenaufruf meldet.
 *
 * Bewusst ohne Zugriff auf `window` oder `document`: Die Entscheidung, welche
 * Adresse als Landingpage gilt, ist die Stelle, an der die gesamte
 * Kampagnenauswertung hängt, und sie soll sich ohne Browser prüfen lassen.
 */

/**
 * Trägt diese Adresse eine eigene Zuordnung?
 *
 * gclid, gbraid und wbraid hängt Google Ads an den Anzeigenlink; utm_source
 * ist die Kennzeichnung, die wir selbst an Links aus anderen Kanälen setzen.
 * Eine Adresse ohne eines dieser Merkmale sagt nichts darüber aus, wodurch
 * jemand hergefunden hat, und ist deshalb auch nichts, was man aufheben müsste.
 */
export function traegtZuordnung(adresse: string): boolean {
  try {
    const parameter = new URL(adresse).searchParams;
    return (
      parameter.has("gclid") ||
      parameter.has("gbraid") ||
      parameter.has("wbraid") ||
      parameter.has("utm_source")
    );
  } catch {
    return false;
  }
}

export interface Herkunft {
  landing: string;
  referrer: string;
  wiederhergestellt: boolean;
}

/**
 * Entscheidet, welche Herkunft dieser Aufruf meldet.
 *
 * Übernommen wird die gemerkte Adresse nur, wenn beim letzten Mal tatsächlich
 * eine Zuordnung ankam und dieser Aufruf keine eigene mitbringt. Ohne diese
 * Bedingung tritt der Fall ein, der die Herkunft im Leadportal unbrauchbar
 * macht: Wer erst /anfrage/gartenarbeit/ aufruft und später
 * /anfrage/spanndecken/, bekommt für die Spanndecken-Anfrage die
 * Gartenarbeit-Seite als Landingpage eingetragen -- und dabei wird nicht
 * einmal etwas bewahrt, denn die erste Seite trug ja selbst keine Zuordnung.
 * Die Merkung überlebt im localStorage beliebig lange, der falsche Eintrag
 * wäre also der Normalfall und nicht die Ausnahme.
 */
export function herkunftWaehlen(
  adresse: string,
  referrer: string,
  gemerkt: { landing?: string; referrer?: string } | null,
): Herkunft {
  const gemerkteAdresse = gemerkt?.landing;

  if (
    gemerkteAdresse !== undefined &&
    !traegtZuordnung(adresse) &&
    traegtZuordnung(gemerkteAdresse)
  ) {
    return {
      landing: gemerkteAdresse,
      referrer: gemerkt?.referrer ?? "",
      wiederhergestellt: true,
    };
  }

  return { landing: adresse, referrer, wiederhergestellt: false };
}

/**
 * Ergänzt die Kennzeichnung einer Einbettung.
 *
 * Ein Quiz, das in einem Ratgeber steckt, läuft auf einer Adresse ohne
 * Parameter - der Beitrag soll ja sauber indexiert werden und nicht unter
 * einer Adresse voller utm-Anhängsel. Ohne Ergänzung käme jede Anfrage von
 * dort als "direkt" an, und der Beitrag, der sie gebracht hat, bekäme sie nie
 * zugeschrieben. Der Behälter nennt deshalb seine Herkunft selbst, und sie
 * wird hier an die gemeldete Landingpage gehängt.
 *
 * Ein echter Anzeigenklick geht vor: Trägt die Adresse schon eine Zuordnung,
 * bleibt sie unangetastet. Sonst überschriebe der Ratgeber die gclid, über
 * die jemand auf ihn gestoßen ist.
 */
export function adresseMitHerkunft(adresse: string, zusatz: string): string {
  if (zusatz === "" || traegtZuordnung(adresse)) return adresse;
  try {
    const url = new URL(adresse);
    for (const [name, wert] of new URLSearchParams(zusatz)) {
      if (!url.searchParams.has(name)) url.searchParams.set(name, wert);
    }
    return url.toString();
  } catch {
    return adresse;
  }
}
